// Step 3: build the zones, with the client (on site or on a video call). Three ways:
// draw round an area on the map, load the client's boundary file, or type in corner
// points their surveyors measured. A small zone is never blocked, but a warning pops up
// as soon as one is put in (agreed 29 Sep).

import { useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import type { Zone } from '../../../../../spec/src/formats.ts';
import { MAX_SITE_ZONES, SMALL_ZONE_METRES, zoneList, zoneWidthMetres, type SiteDraft } from '../../../../src/draft.ts';
import { api, message } from '../../api.ts';
import { readBoundaryFile, readTypedPoints, type LonLat, type Shape } from '../../geo.ts';
import { mapSize, project, type BBox } from '../../map.tsx';
import type { SiteView } from '../../types.ts';
import { Dialog, Link, useSaveWhenTypingStops } from '../../ui.tsx';

const nextId = (zones: Zone[]): string | null => {
  for (let i = 1; i <= MAX_SITE_ZONES; i++) {
    const id = String(i).padStart(2, '0');
    if (!zones.some((z) => z.id === id)) return id;
  }
  return null;
};
const sizeText = (z: Zone): string => {
  const lats = z.polygon.map((p) => p[1]);
  const lons = z.polygon.map((p) => p[0]);
  const mid = (((Math.min(...lats) + Math.max(...lats)) / 2) * Math.PI) / 180;
  const w = (Math.max(...lons) - Math.min(...lons)) * 111.32 * Math.cos(mid);
  const h = (Math.max(...lats) - Math.min(...lats)) * 110.574;
  return w < 1 && h < 1 ? `${Math.round(w * 1000)} × ${Math.round(h * 1000)} m` : `${w.toFixed(1)} × ${h.toFixed(1)} km`;
};
const outside = (z: Zone, b: BBox) => z.polygon.some(([lon, lat]) => lon < b[0] || lon > b[2] || lat < b[1] || lat > b[3]);

type Pending = { zones: Zone[]; small: { zone: Zone; metres: number }[]; onChange: () => void };

export function ZonesStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  const bbox = draft.map?.bbox as BBox | undefined;
  const [zones, setZones] = useState<Zone[]>(draft.zones);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<'idle' | 'draw' | 'type'>('idle');
  const [drawn, setDrawn] = useState<LonLat[]>([]);
  const [naming, setNaming] = useState<{ points: LonLat[]; name: string; from: 'draw' | 'type' } | null>(null);
  const [typed, setTyped] = useState({ name: '', text: '' });
  const [importing, setImporting] = useState<{ file: string; shapes: Shape[]; include: boolean[]; names: string[] } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ zone: string; index: number } | null>(null);
  // Zone names save themselves once typing pauses, and when the box loses focus.
  const autosave = useSaveWhenTypingStops(() => save(zones));

  if (!bbox) {
    return (
      <div className="card">
        <p className="muted">Put the site on the map first, so the zones can be drawn on it.</p>
        <Link to={`/sites/${site.siteId}/setup/map`} className="btn primary small" style={{ alignSelf: 'flex-start' }}>
          Go to the map step
        </Link>
      </div>
    );
  }
  const size = mapSize(bbox);

  const save = async (next: Zone[]) => {
    const before = zones;
    setZones(next);
    setError(null);
    setStatus('Saving…');
    try {
      await api('PUT', `/sites/${site.siteId}/draft`, { clientName: site.clientName, siteName: site.siteName, timeZone: site.timeZone, draft: { ...(site.draft as SiteDraft), zones: next } });
      setStatus("Saved · nothing is live until it's approved on the ops tablet");
      reload();
    } catch (e) {
      setZones(before);
      setStatus(null);
      setError(message(e));
    }
  };

  /** Adds zones; small ones first bring up the warning. */
  const add = (fresh: { points: LonLat[]; name: string }[], onChange: () => void) => {
    const all = [...zones];
    const made: Zone[] = [];
    for (const f of fresh) {
      const id = nextId(all);
      if (!id) {
        setError(`A site can have up to ${MAX_SITE_ZONES} zones.`);
        return;
      }
      const zone: Zone = { id, name: f.name.trim() || `Zone ${id}`, polygon: f.points };
      all.push(zone);
      made.push(zone);
    }
    const small = made.map((zone) => ({ zone, metres: Math.round(zoneWidthMetres(zone)) })).filter((s) => s.metres < SMALL_ZONE_METRES);
    if (small.length) setPending({ zones: all, small, onChange });
    else {
      void save(all);
      setSelected(made[made.length - 1]?.id ?? null);
    }
  };

  // ---- Map pointer handling ----
  const toLonLat = (clientX: number, clientY: number): LonLat | null => {
    const ctm = svgRef.current?.getScreenCTM();
    if (!ctm) return null;
    const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    return [bbox[0] + (pt.x / size.w) * (bbox[2] - bbox[0]), bbox[3] - (pt.y / size.h) * (bbox[3] - bbox[1])];
  };
  const onMapClick = (e: ReactMouseEvent) => {
    if (mode !== 'draw') return;
    const at = toLonLat(e.clientX, e.clientY);
    if (!at) return;
    if (drawn.length >= 3) {
      const [fx, fy] = project(bbox, drawn[0][0], drawn[0][1]);
      const [cx, cy] = project(bbox, at[0], at[1]);
      if (Math.hypot(fx - cx, fy - cy) < 14) return finishDrawing();
    }
    setDrawn([...drawn, at]);
  };
  const finishDrawing = () => {
    if (drawn.length < 3) return;
    setNaming({ points: drawn, name: '', from: 'draw' });
  };
  const startVertexDrag = (zone: string, index: number) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    svgRef.current?.setPointerCapture(e.pointerId);
    drag.current = { zone, index };
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    const at = d && toLonLat(e.clientX, e.clientY);
    if (!d || !at) return;
    setZones((zs) => zs.map((z) => (z.id === d.zone ? { ...z, polygon: z.polygon.map((p, i) => (i === d.index ? at : p)) } : z)));
  };
  const endDrag = () => {
    if (drag.current) {
      drag.current = null;
      const moved = zones;
      const z = moved.find((x) => x.id === selected);
      if (z && zoneWidthMetres(z) < SMALL_ZONE_METRES) setPending({ zones: moved, small: [{ zone: z, metres: Math.round(zoneWidthMetres(z)) }], onChange: () => undefined });
      else void save(moved);
    }
  };

  const rename = (id: string, name: string) => {
    const next = zones.map((z) => (z.id === id ? { ...z, name } : z));
    setZones(next);
    setStatus(null);
    autosave.edited();
  };
  const remove = (id: string) => {
    if (!confirm(`Remove zone ${id}?`)) return;
    setSelected(null);
    void save(zones.filter((z) => z.id !== id));
  };

  const addTyped = () => {
    const r = readTypedPoints(typed.text);
    if (r.problem) {
      setError(r.problem);
      return;
    }
    setError(null);
    add([{ points: r.points, name: typed.name }], () => setMode('type'));
    setMode('idle');
  };

  const chooseFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const shapes = await readBoundaryFile(file);
      if (shapes.length === 0) throw new Error('No shapes were found in this file.');
      setImporting({ file: file.name, shapes, include: shapes.map((s) => s.kind === 'area'), names: shapes.map((s) => s.name) });
    } catch (e) {
      setError(message(e));
    }
  };
  const addImported = () => {
    if (!importing) return;
    const picked = importing.shapes.map((s, i) => ({ s, i })).filter(({ s, i }) => s.kind === 'area' && importing.include[i]);
    if (zones.length + picked.length > MAX_SITE_ZONES) {
      setError(`That would make ${zones.length + picked.length} zones; a site can have up to ${MAX_SITE_ZONES}.`);
      return;
    }
    setImporting(null);
    add(
      picked.map(({ s, i }) => ({ points: s.points, name: importing.names[i] })),
      () => undefined,
    );
  };

  const badEdges = zones.filter((z) => outside(z, bbox));
  const small = zones.map((z) => ({ z, m: Math.round(zoneWidthMetres(z)) })).filter((x) => x.m < SMALL_ZONE_METRES);
  const picture = draft.picture ? `/console-api/pictures/${draft.picture.sha256}` : null;
  const poly = (pts: LonLat[]) => pts.map((p) => project(bbox, p[0], p[1]).join(',')).join(' ');

  return (
    <>
      <div className="row wrap">
        <button className={mode === 'draw' ? 'btn small primary' : 'btn small'} onClick={() => { setMode(mode === 'draw' ? 'idle' : 'draw'); setDrawn([]); setSelected(null); }}>
          Draw a zone
        </button>
        <label className="btn small">
          Load boundary file
          <input type="file" accept=".kml,.kmz,.geojson,.json,.zip" className="visually-hidden" onChange={(e) => { void chooseFile(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <button className={mode === 'type' ? 'btn small primary' : 'btn small'} onClick={() => setMode(mode === 'type' ? 'idle' : 'type')}>
          Type corner points
        </button>
        <span className="spacer" />
        <span className="faint small">{status}</span>
      </div>
      {error && (
        <div className="note red" role="alert">
          {error}
        </div>
      )}

      <div className="split wide-left">
        <div className="stack">
          <div className="editor">
            <svg ref={svgRef} viewBox={`0 0 ${size.w} ${size.h}`} onClick={onMapClick} onPointerMove={onMove} onPointerUp={endDrag} onPointerCancel={endDrag} style={{ cursor: mode === 'draw' ? 'crosshair' : 'default' }} role="img" aria-label="The site's zones on its map">
              {picture ? <image href={picture} x="0" y="0" width={size.w} height={size.h} preserveAspectRatio="none" style={{ filter: 'brightness(0.8)' }} /> : <rect x="0" y="0" width={size.w} height={size.h} fill="#0A0C0F" />}
              {zones.map((z) => {
                const pts = z.polygon.map((p) => project(bbox, p[0], p[1]));
                const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
                const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
                const on = selected === z.id;
                const tiny = zoneWidthMetres(z) < SMALL_ZONE_METRES;
                return (
                  <g key={z.id} onClick={(e) => { if (mode !== 'draw') { e.stopPropagation(); setSelected(z.id); } }} style={{ cursor: mode === 'draw' ? 'crosshair' : 'pointer' }}>
                    <polygon points={poly(z.polygon)} fill={on ? 'rgba(255,255,255,0.16)' : 'rgba(61,214,140,0.14)'} stroke={on ? '#FFFFFF' : tiny ? '#F5B544' : '#3DD68C'} strokeWidth={on ? 3 : 2} />
                    <text className="zone-label" x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                      {z.id}
                    </text>
                  </g>
                );
              })}
              {selected &&
                mode !== 'draw' &&
                zones
                  .find((z) => z.id === selected)
                  ?.polygon.map((p, i) => {
                    const [x, y] = project(bbox, p[0], p[1]);
                    return <circle key={i} cx={x} cy={y} r="9" className="handle" onPointerDown={startVertexDrag(selected, i)} />;
                  })}
              {mode === 'draw' && drawn.length > 0 && (
                <>
                  <polyline points={poly(drawn)} fill="rgba(255,255,255,0.12)" stroke="#FFFFFF" strokeWidth="2.5" strokeDasharray="10 6" />
                  {drawn.map((p, i) => {
                    const [x, y] = project(bbox, p[0], p[1]);
                    return <circle key={i} cx={x} cy={y} r={i === 0 && drawn.length >= 3 ? 11 : 7} fill="#FFFFFF" stroke="#050607" strokeWidth="2" />;
                  })}
                </>
              )}
            </svg>
            {mode === 'draw' && (
              <span className="chip-on-map" style={{ left: '50%', bottom: 14, transform: 'translateX(-50%)', maxWidth: 'calc(100% - 28px)', textAlign: 'center' }}>
                Click round the area. Click the first point again to close it.
              </span>
            )}
          </div>
          {mode === 'draw' && (
            <div className="row">
              <button className="btn primary small" disabled={drawn.length < 3} onClick={finishDrawing}>
                Finish the zone
              </button>
              <button className="btn small" disabled={drawn.length === 0} onClick={() => setDrawn(drawn.slice(0, -1))}>
                Undo last point
              </button>
              <button className="btn small" onClick={() => { setMode('idle'); setDrawn([]); }}>
                Cancel
              </button>
            </div>
          )}
          {selected && mode !== 'draw' && <p className="faint small">Drag the white points to move zone {selected}'s corners.</p>}
        </div>

        <div className="stack">
          {mode === 'type' && (
            <div className="card">
              <span style={{ fontWeight: 600 }}>Type corner points</span>
              <span className="muted small">For areas the client's surveyors have already measured: one corner per line, as latitude, longitude.</span>
              <label className="field">
                Zone name
                <input className="input" value={typed.name} onChange={(e) => setTyped({ ...typed, name: e.target.value })} />
              </label>
              <label className="field">
                Corners
                <textarea className="input mono" rows={5} placeholder={'30.4400, 47.3000\n30.4400, 47.3100\n30.4330, 47.3100\n30.4330, 47.3000'} value={typed.text} onChange={(e) => setTyped({ ...typed, text: e.target.value })} />
              </label>
              <button className="btn primary small" style={{ alignSelf: 'flex-start' }} onClick={addTyped}>
                Add as a zone
              </button>
            </div>
          )}
          <div className="card" style={{ gap: 4 }}>
            <div className="row" style={{ justifyContent: 'space-between', paddingBottom: 6 }}>
              <span style={{ fontWeight: 600 }}>Zones</span>
              <span className="muted small">
                {zones.length} of {MAX_SITE_ZONES}
              </span>
            </div>
            {zones.length === 0 && <span className="faint">None yet</span>}
            {zones.map((z) => (
              <div key={z.id} className="row" style={{ gap: 8, padding: '4px 0', background: selected === z.id ? 'var(--raised)' : 'transparent', borderRadius: 10 }}>
                <button className="linkish mono" style={{ width: 32, textDecoration: 'none', color: 'var(--text2)', fontSize: 15 }} onClick={() => setSelected(z.id)} aria-label={`Select zone ${z.id}`}>
                  {z.id}
                </button>
                <input className="input" style={{ height: 36, flex: 1, minWidth: 0 }} aria-label={`Name of zone ${z.id}`} value={z.name} onChange={(e) => rename(z.id, e.target.value)} onBlur={autosave.flush} />
                <span className={zoneWidthMetres(z) < SMALL_ZONE_METRES ? 'amber small' : 'faint small'} style={{ width: 92, textAlign: 'right' }}>
                  {sizeText(z)}
                </span>
                <button className="linkish" onClick={() => remove(z.id)} aria-label={`Remove zone ${z.id}`}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="card" style={{ gap: 10 }}>
            <span style={{ fontWeight: 600 }}>Checks</span>
            {badEdges.length ? (
              <span className="red small">
                {zoneList(badEdges.map((z) => z.id))} {badEdges.length === 1 ? 'goes' : 'go'} outside the map area. Move {badEdges.length === 1 ? 'its' : 'their'} corners in, or make the map area bigger in step 2.
              </span>
            ) : (
              <span className="green small">Every zone fits inside the map area.</span>
            )}
            {small.length ? (
              small.map(({ z, m }) => (
                <span key={z.id} className="amber small">
                  Zone {z.id} · {z.name} is small (about {m} m), kept on purpose. Phones up to about 100 m outside it may get its alerts too.
                </span>
              ))
            ) : (
              <span className="green small">No zone is under {SMALL_ZONE_METRES} m across.</span>
            )}
            <span className="amber small">Outside every zone, phones only get a quiet notice, never the siren. Check the zones cover everywhere people work.</span>
          </div>
        </div>
      </div>

      {naming && (
        <Dialog title={`Name zone ${nextId(zones) ?? ''}`} onClose={() => setNaming(null)}>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const n = naming;
              setNaming(null);
              setMode('idle');
              setDrawn([]);
              add([{ points: n.points, name: n.name }], () => {
                setMode('draw');
                setDrawn(n.points);
              });
            }}
          >
            <p className="muted">What staff call this area, for example “South wellpad”. Staff see it on their lock screen.</p>
            <input className="input" autoFocus value={naming.name} onChange={(e) => setNaming({ ...naming, name: e.target.value })} required maxLength={40} />
            <div className="row">
              <button className="btn primary" type="submit">
                Add the zone
              </button>
              <button className="btn" type="button" onClick={() => setNaming(null)}>
                Back
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {pending && (
        <Dialog title={pending.small.length === 1 ? 'This zone is small' : 'Some zones are small'} onClose={() => setPending(null)}>
          {pending.small.map(({ zone, metres }) => (
            <p key={zone.id} style={{ fontWeight: 500 }}>
              Zone {zone.id} · {zone.name} is only about {metres} m across.
            </p>
          ))}
          <p className="muted">Phones know where they are to within about 10–50 m, and near a zone's edge they count themselves as inside so nobody is missed. So phones up to about 100 m outside a small zone may get its alerts too.</p>
          <p className="muted">It will still work. Keep it if that's what the client wants.</p>
          <div className="row">
            <button
              className="btn primary"
              onClick={() => {
                const p = pending;
                setPending(null);
                void save(p.zones);
                setSelected(p.small[p.small.length - 1].zone.id);
              }}
            >
              {pending.small.length === 1 ? 'Keep it' : 'Keep them'}
            </button>
            <button
              className="btn"
              onClick={() => {
                const p = pending;
                setPending(null);
                setZones(draft.zones);
                p.onChange();
              }}
            >
              Change it
            </button>
          </div>
        </Dialog>
      )}

      {importing && (
        <Dialog title="Load boundary file" onClose={() => setImporting(null)}>
          <p className="muted small">
            <span className="mono">{importing.file}</span> · {importing.shapes.length} shape{importing.shapes.length === 1 ? '' : 's'} found. Closed areas can become zones; lines and points can't.
          </p>
          <div className="stack" style={{ gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {importing.shapes.map((s, i) => (
              <div key={i} className="row" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  aria-label={`Use ${s.name}`}
                  disabled={s.kind !== 'area'}
                  checked={s.kind === 'area' && importing.include[i]}
                  onChange={(e) => setImporting({ ...importing, include: importing.include.map((v, j) => (j === i ? e.target.checked : v)) })}
                />
                {s.kind === 'area' ? (
                  <input className="input" style={{ height: 36, flex: 1 }} aria-label="Zone name" value={importing.names[i]} onChange={(e) => setImporting({ ...importing, names: importing.names.map((v, j) => (j === i ? e.target.value : v)) })} />
                ) : (
                  <span className="faint small" style={{ flex: 1 }}>
                    {s.name} · {s.kind}: skipped, only closed areas can be zones
                  </span>
                )}
                {s.kind === 'area' && outside({ id: '', name: '', polygon: s.points }, bbox) && <span className="amber small">outside the map</span>}
              </div>
            ))}
          </div>
          <p className="faint small">Reads KML and KMZ (Google Earth) and GeoJSON. CAD drawings and PDF plans usually have no map coordinates, so we trace those by eye.</p>
          <div className="row">
            <button className="btn primary" onClick={addImported}>
              Add {importing.shapes.filter((s, i) => s.kind === 'area' && importing.include[i]).length} zones
            </button>
            <button className="btn" onClick={() => setImporting(null)}>
              Cancel
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
